import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import tarotCardBack from "@/assets/tarot-card-back.jpg";

interface TarotCardProps {
  index: number;
  isSelected: boolean;
  isRevealed: boolean;
  revealedCard?: {
    name: string;
    image: string;
    position: string;
    meaning: string;
  };
  onSelect: (index: number) => void;
  disabled: boolean;
  revealDelay?: number;
}

const TarotCard = ({
  index,
  isSelected,
  isRevealed,
  revealedCard,
  onSelect,
  disabled,
  revealDelay = 0,
}: TarotCardProps) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showGlow, setShowGlow] = useState(false);

  useEffect(() => {
    if (isRevealed && revealedCard) {
      const timer = setTimeout(() => {
        setIsFlipped(true);
        setShowGlow(true);
      }, revealDelay);
      return () => clearTimeout(timer);
    }
  }, [isRevealed, revealedCard, revealDelay]);

  const handleClick = () => {
    if (!disabled && !isRevealed) {
      onSelect(index);
    }
  };

  return (
    <div
      className={cn(
        "relative cursor-pointer transition-all duration-500",
        isSelected && !isRevealed && "scale-105 z-10",
        isHovering && !disabled && !isRevealed && "scale-102",
        disabled && !isSelected && "opacity-50 cursor-not-allowed"
      )}
      style={{ perspective: "1000px" }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Mystical glow effect */}
      {(isSelected || showGlow) && (
        <div
          className={cn(
            "absolute -inset-2 rounded-xl blur-xl transition-opacity duration-700",
            isSelected && !isRevealed
              ? "bg-gradient-to-r from-primary/60 via-secondary/60 to-primary/60 animate-pulse"
              : "bg-gradient-to-r from-yellow-500/40 via-primary/40 to-purple-500/40"
          )}
        />
      )}

      {/* Floating particles for selected cards */}
      {isSelected && !isRevealed && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-primary rounded-full animate-float-particle"
              style={{
                left: `${20 + i * 12}%`,
                animationDelay: `${i * 0.3}s`,
                animationDuration: `${2 + i * 0.5}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Card container with 3D flip */}
      <div
        className={cn(
          "relative aspect-[2/3] w-full transition-transform duration-700 ease-out",
          isFlipped && "rotate-y-180"
        )}
        style={{
          transformStyle: "preserve-3d",
          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Card Back */}
        <div
          className={cn(
            "absolute inset-0 rounded-lg overflow-hidden border-2 transition-all duration-300",
            isSelected && !isRevealed
              ? "border-primary shadow-[0_0_30px_rgba(var(--primary),0.5)]"
              : "border-border/50 hover:border-primary/50"
          )}
          style={{
            backfaceVisibility: "hidden",
          }}
        >
          <img
            src={tarotCardBack}
            alt="Carta de Tarot"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          
          {/* Shimmer effect on hover */}
          {isHovering && !disabled && !isRevealed && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
          )}
          
          {/* Selection indicator */}
          {isSelected && !isRevealed && (
            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-primary/30 backdrop-blur-sm flex items-center justify-center animate-pulse">
                <span className="text-2xl">✨</span>
              </div>
            </div>
          )}
        </div>

        {/* Card Front (Revealed) */}
        {revealedCard && (
          <div
            className="absolute inset-0 rounded-lg overflow-hidden border-2 border-yellow-500/50 shadow-[0_0_40px_rgba(234,179,8,0.3)]"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            <img
              src={revealedCard.image}
              alt={revealedCard.name}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            
            {/* Name overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-3">
              <p className="text-white font-bold text-center text-sm md:text-base">
                {revealedCard.name}
              </p>
              <p className="text-yellow-400 text-xs text-center">
                {revealedCard.position}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TarotCard;
